// WeatherTile.jsx
//
// Weather tile for the dashboard. Shows current conditions for Orlando (WDW)
// and/or Anaheim (Disneyland) depending on which parks the device subscribes
// to. When both apply, the tile rotates between them every ~10s.
//
// Data comes from RTDB (weather/orlando, weather/anaheim), written by the
// ride synchronizer's weather thread every ~10 min. The browser never calls
// a weather API directly.
//
// ─── Which location(s) show ──────────────────────────────────────────────
//   any Orlando park (MK/EPCOT/HS/AK), no DL/CA      -> Orlando only
//   any Orlando park AND any of DL/CA                -> both (rotate)
//   only DL/CA (one or both), no Orlando             -> Anaheim only
//   When both: starts on Orlando, rotates every 10s.
//
// ─── Data dependency (Firebase RTDB) ──────────────────────────────────────
//   weather/orlando = { tempF, code, isDay, updatedAt }
//   weather/anaheim = { tempF, code, isDay, updatedAt }
//   `code` is the raw Open-Meteo WMO weather code.
//
// ─── Props ────────────────────────────────────────────────────────────────
//   database            (required)  Firebase Database instance.
//   subscribedParkKeys  (optional)   Array of park codes; null/undefined =
//                                    all parks (-> both locations).
//   style               (optional)   merged onto the outer tile container.
//
// REQUIRES in /public:  /static/vip/day.jpg  and  /static/vip/night.jpg

import { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue, off } from "firebase/database";

const ROTATE_MS = 10000;

const ORLANDO_PARKS = ["MK", "EPCOT", "HS", "AK"];
const ANAHEIM_PARKS = ["DL", "CA"];

// Border color denoting which location is currently shown.
const BORDER_ORLANDO = "#0B3FA5"; // dark blue (WDW / Magic Kingdom)
const BORDER_ANAHEIM = "#d94a7c"; // pink (Disneyland)

// ── Open-Meteo WMO weather code -> label + emoji icon ───────────────────────
// Ported verbatim from the prototype's weatherCodeToUi.
function weatherCodeToUi(code, isDay) {
  if (code === 0)
    return isDay
      ? { label: "Sunny", icon: "\u2600\uFE0F" }
      : { label: "Clear", icon: "\uD83C\uDF19" };
  if (code === 1 || code === 2)
    return isDay
      ? { label: "Partly Cloudy", icon: "\u26C5\uFE0F" }
      : { label: "Partly Cloudy", icon: "\u2601\uFE0F\uD83C\uDF19" };
  if (code === 3) return { label: "Cloudy", icon: "\u2601\uFE0F" };
  if (code === 45 || code === 48) return { label: "Fog", icon: "\uD83C\uDF2B\uFE0F" };
  if ([51, 53, 55, 56, 57].includes(code))
    return { label: "Drizzle", icon: "\uD83C\uDF26\uFE0F" };
  if ([61, 63, 65, 66, 67].includes(code))
    return { label: "Rain", icon: "\uD83C\uDF27\uFE0F" };
  if ([71, 73, 75, 77].includes(code))
    return { label: "Snow", icon: "\uD83C\uDF28\uFE0F" };
  if ([80, 81, 82].includes(code))
    return { label: "Showers", icon: "\uD83C\uDF26\uFE0F" };
  if ([95, 96, 99].includes(code))
    return { label: "Storms", icon: "\u26C8\uFE0F" };
  return { label: "Weather", icon: isDay ? "\uD83C\uDF24\uFE0F" : "\uD83C\uDF19" };
}

const WeatherTile = ({ database, subscribedParkKeys, style }) => {
  const [orlando, setOrlando] = useState(null); // { tempF, code, isDay }
  const [anaheim, setAnaheim] = useState(null);

  // ── Subscribe to both weather nodes ──────────────────────────────────
  useEffect(() => {
    if (!database) return;
    const oRef = ref(database, "weather/orlando");
    const aRef = ref(database, "weather/anaheim");

    const oH = onValue(oRef, (snap) => setOrlando(snap.exists() ? snap.val() : null));
    const aH = onValue(aRef, (snap) => setAnaheim(snap.exists() ? snap.val() : null));

    return () => {
      off(oRef);
      off(aRef);
      if (typeof oH === "function") oH();
      if (typeof aH === "function") aH();
    };
  }, [database]);

  // ── Which locations apply, based on subscribed parks ─────────────────
  const locations = useMemo(() => {
    // null/undefined subscribedParkKeys = all parks -> both.
    const keys = Array.isArray(subscribedParkKeys)
      ? subscribedParkKeys
      : [...ORLANDO_PARKS, ...ANAHEIM_PARKS];

    const hasOrlando = keys.some((k) => ORLANDO_PARKS.includes(k));
    const hasAnaheim = keys.some((k) => ANAHEIM_PARKS.includes(k));

    const out = [];
    if (hasOrlando) out.push("orlando"); // Orlando first when both
    if (hasAnaheim) out.push("anaheim");
    // Fallback: if somehow neither matched, show Orlando.
    return out.length ? out : ["orlando"];
  }, [subscribedParkKeys]);

  // ── Rotation between locations (only when 2) ─────────────────────────
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  // Reset to first location whenever the location set changes.
  useEffect(() => {
    setIdx(0);
  }, [locations.length]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (locations.length < 2) return; // nothing to rotate
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % locations.length);
    }, ROTATE_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [locations.length]);

  // ── Current location + its data ──────────────────────────────────────
  const activeLoc = locations[Math.min(idx, locations.length - 1)] || "orlando";
  const data = activeLoc === "anaheim" ? anaheim : orlando;

  const isDay = data ? data.isDay !== false : true;
  const code = data ? data.code : null;
  const tempF = data && typeof data.tempF === "number" ? data.tempF : null;

  const ui = weatherCodeToUi(code, isDay);
  const headerText = activeLoc === "anaheim" ? "ANAHEIM WEATHER" : "ORLANDO WEATHER";
  const borderColor = activeLoc === "anaheim" ? BORDER_ANAHEIM : BORDER_ORLANDO;
  const bgImg = isDay ? "/static/vip/day.jpg" : "/static/vip/night.jpg";

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 20,
        overflow: "hidden",
        // 3px border denotes the active location (blue=Orlando, pink=Anaheim)
        border: `3px solid ${borderColor}`,
        boxShadow: "0 10px 28px rgba(11,31,53,0.16)",
        background: "#0b1f35",
        ...style,
      }}
    >
      {/* Day/night background image */}
      <img
        src={bgImg}
        alt=""
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      {/* Legibility scrim */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.22) 70%, rgba(0,0,0,0.34) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 12px",
          fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 1,
            color: isDay ? "#0b1f35" : "#ffffff",
            textShadow: isDay ? "none" : "0 1px 4px rgba(0,0,0,0.6)",
            textTransform: "uppercase",
          }}
        >
          {headerText}
        </div>

        {/* Temp + icon */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 52,
              fontWeight: 800,
              lineHeight: 1,
              color: isDay ? "#0b1f35" : "#ffffff",
              textShadow: isDay ? "none" : "0 1px 6px rgba(0,0,0,0.6)",
            }}
          >
            {tempF != null ? `${tempF}\u00B0` : "\u2014"}
          </span>
          <span style={{ fontSize: 34, lineHeight: 1 }}>{ui.icon}</span>
        </div>

        {/* Condition label */}
        <div
          style={{
            fontSize: 17,
            fontWeight: 800,
            color: isDay ? "#0b1f35" : "#ffffff",
            textShadow: isDay ? "none" : "0 1px 4px rgba(0,0,0,0.6)",
          }}
        >
          {ui.label}
        </div>
      </div>
    </div>
  );
};

export default WeatherTile;
